export type OrderbookVersionClassification = 'stale' | 'next' | 'gap';

export type OrderbookProtocolV2MessageType =
  | 'orderbook_connected'
  | 'orderbook_update'
  | 'pool_state_refresh'
  | 'orderbook_resync_required'
  | 'orderbook_pong';

export interface OrderbookProtocolV2Base {
  type: OrderbookProtocolV2MessageType;
  protocol_version: 2;
}

export interface OrderbookConnectedV2Message extends OrderbookProtocolV2Base {
  type: 'orderbook_connected';
  pool_state_version: number;
  season_id: number;
  pool_code: string;
  competition_code: string;
  season_code: string;
}

export interface OrderbookUpdateV2Message extends OrderbookProtocolV2Base {
  type: 'orderbook_update';
  pool_state_version: number;
  season_id: number;
  pool_code: string;
  season_team_id: number;
  full_ladder: { bid: unknown[]; ask: unknown[] };
  top_of_book: { bid: unknown | null; ask: unknown | null };
  orderbook_version?: number;
  last_changed_pool_state_version?: number;
  [key: string]: unknown;
}

export interface PoolStateRefreshV2Message extends OrderbookProtocolV2Base {
  type: 'pool_state_refresh';
  pool_state_version: number;
  season_id: number;
  pool_code: string;
  reason: string;
  season_team_id?: number;
  affected_season_team_ids?: number[];
}

export interface OrderbookResyncRequiredV2Message extends OrderbookProtocolV2Base {
  type: 'orderbook_resync_required';
  reason: string;
}

export interface OrderbookPongV2Message extends OrderbookProtocolV2Base {
  type: 'orderbook_pong';
  pool_state_version: number;
}

export type OrderbookProtocolV2Message =
  | OrderbookConnectedV2Message
  | OrderbookUpdateV2Message
  | PoolStateRefreshV2Message
  | OrderbookResyncRequiredV2Message
  | OrderbookPongV2Message;

export function classifyOrderbookStateVersion(
  installedVersion: number,
  incomingVersion: number,
): OrderbookVersionClassification {
  if (incomingVersion <= installedVersion) return 'stale';
  if (incomingVersion === installedVersion + 1) return 'next';
  return 'gap';
}

export function shouldInstallOrderbookSnapshot(
  installedVersion: number,
  snapshotVersion: number,
): boolean {
  return Number.isSafeInteger(snapshotVersion) && snapshotVersion >= installedVersion;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isNonNegativeSafeInteger(value: unknown): value is number {
  return Number.isSafeInteger(value) && Number(value) >= 0;
}

function isPositiveSafeInteger(value: unknown): value is number {
  return Number.isSafeInteger(value) && Number(value) > 0;
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

export function isOrderbookProtocolV2Message(
  value: unknown,
): value is OrderbookProtocolV2Message {
  if (!isRecord(value) || value.protocol_version !== 2 || !isNonEmptyString(value.type)) {
    return false;
  }

  if (value.type === 'orderbook_resync_required') {
    return isNonEmptyString(value.reason);
  }
  if (value.type === 'orderbook_pong') {
    return isNonNegativeSafeInteger(value.pool_state_version);
  }
  if (value.type === 'orderbook_connected') {
    return (
      isNonNegativeSafeInteger(value.pool_state_version) &&
      isPositiveSafeInteger(value.season_id) &&
      isNonEmptyString(value.pool_code) &&
      isNonEmptyString(value.competition_code) &&
      isNonEmptyString(value.season_code)
    );
  }
  if (value.type === 'orderbook_update') {
    return (
      isNonNegativeSafeInteger(value.pool_state_version) &&
      isPositiveSafeInteger(value.season_id) &&
      isNonEmptyString(value.pool_code) &&
      isPositiveSafeInteger(value.season_team_id) &&
      isRecord(value.full_ladder) &&
      Array.isArray(value.full_ladder.bid) &&
      Array.isArray(value.full_ladder.ask) &&
      isRecord(value.top_of_book)
    );
  }
  if (value.type === 'pool_state_refresh') {
    const hasSingularTeam = isPositiveSafeInteger(value.season_team_id);
    const hasAffectedTeams =
      Array.isArray(value.affected_season_team_ids) &&
      value.affected_season_team_ids.length > 0 &&
      value.affected_season_team_ids.every(isPositiveSafeInteger);
    return (
      isNonNegativeSafeInteger(value.pool_state_version) &&
      isPositiveSafeInteger(value.season_id) &&
      isNonEmptyString(value.pool_code) &&
      isNonEmptyString(value.reason) &&
      (hasSingularTeam || hasAffectedTeams)
    );
  }
  return false;
}
